// ─────────────────────────────────────────────────────────────
// SCOUT LEADER READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// Same structure as Martial Arts Kids Class Ready / ABA Session Ready (modeled on Managers), with a NEW palette
// (forest green sections, lime buttons, campfire amber highlights, sunrise orange labels), NEW button copy, a NEW
// skills band and a NEW section for group coordinators, to test what converts.
// Promise (must match ads, checkout and the PDF): 40-meeting year plan + 10 complete 90-minute meetings for kids 8–11,
// $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Generic: no association names, ranks, badges, uniforms or official programs. No testimonials.
// Copy describes what the Gamma Studio book really has (no exact cue quotes that differ from the PDF).
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://scout-leader-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'Scout Leader Ready™ | Your Whole Year of Scout Meetings, Already Planned';
export const META_DESCRIPTION =
  'A 40-meeting year plan plus 10 complete, ready-to-run 90-minute scout meetings for kids 8–11. Open this week’s page, prep in 10 minutes and run it tonight.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'SCOUT LEADER READY™';
/** Label for every button that scrolls to the offer section (new copy for this landing) */
export const ctaLabel = 'GET THIS WEEK’S MEETING READY';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY MEETINGS READY · $27';
export const topBarMobileLabel = '40 MEETINGS · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR VOLUNTEER SCOUT & YOUTH GROUP LEADERS · KIDS 8–11',
  titleLead: 'YOUR WHOLE YEAR OF SCOUT MEETINGS,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 40-meeting year plan plus 10 complete, ready-to-run 90-minute meetings. Open this week’s page and run it tonight.',
  mockupAlt: 'Scout Leader Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '40', label: 'MEETINGS PLANNED' },
  { value: '10', label: 'COMPLETE MEETINGS' },
  { value: '90', label: 'MINUTES, FULLY TIMED' },
  { value: '8–11', label: 'AGES, 2 LEVELS EACH' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: '10 P.M. THE NIGHT BEFORE VS. MEETING NIGHT.',
  before: {
    label: 'BEFORE',
    when: 'After work, the night before',
    quote: '“What are we doing with the kids tomorrow?”',
    items: ['Scrolling for ideas after a long day.', 'The same game again.', 'Bored kids and a flat meeting.'],
  },
  after: {
    label: 'AFTER',
    when: 'Meeting night',
    items: ['Open this week’s page.', 'Prep in 10 minutes.', 'Run the 90-minute meeting block.', 'Close the circle and send the family note.'],
  },
};

// ── 3. WHAT DOES YOUR GROUP NEED THIS WEEK (pick a need, see the meeting that is ready for it) ──
const block = 'Meeting block: welcome & opening game 15 · skill time 35 · team challenge 20 · closing game 10 · closing circle 10';
const close3 = ['A team challenge and a closing game, with diagrams', 'Closing circle and a ready-to-send family note', 'Skill check: what each kid shows'];
export const selector = {
  eyebrow: 'START WHERE YOUR GROUP IS',
  title: 'WHAT DOES YOUR GROUP NEED THIS WEEK?',
  sub: 'Pick a need and see the meeting that is ready for it.',
  startLabel: 'YOUR NEXT MEETING',
  tablistLabel: 'Group needs',
  columns: ['PAGE 1 · MEETING PLAN', 'PAGE 2 · SKILL TIME', 'PAGE 3 · CHALLENGE & CLOSING'],
  note: 'All 10 meetings follow this same 3-page structure. Use your group’s own promise, words and awards.',
  items: [
    {
      tab: 'Getting kids outdoors',
      n: 'MEETING 08 · MEETING 24',
      title: 'Nature Detectives: Tracks, Trees & Leave It Better',
      goal: 'Kids name 3 trees by their leaves and 3 animal tracks, and leave the place better than they found it.',
      open: ['Meeting goal, materials and 10-minute prep', 'Your opening words, word for word', block],
      activity: ['5 steps: look close, leaf detective, track detective, leave it better and the hands-off rule', '3 Fix It cues for the most common problems', 'Safety box and age level card for ages 8–9 and 10–11'],
      close: ['Team challenge: Leave It Better Challenge', 'Closing game, closing circle and a ready-to-send family note', 'Skill check: what each kid shows'],
    },
    {
      tab: 'Knots kids remember',
      n: 'MEETING 02 · MEETING 4',
      title: 'Knot Night: 4 Knots Every Scout Uses',
      goal: 'Overhand, square, figure-eight and clove hitch, and what each knot is for.',
      open: ['Meeting goal, materials and 10-minute prep', 'Opening game: Human Knot', block],
      activity: ['Step-by-step knot diagrams with key points', '3 Fix It cues for the most common knot mistakes', 'Age level card for ages 8–9 and 10–11'],
      close: close3,
    },
    {
      tab: 'First aid basics',
      n: 'MEETING 03 · MEETING 7',
      title: 'First Aid 1: Cuts, Scrapes, Stings & Getting Help',
      goal: 'Check, call, care: cover a pretend scrape and know how to call for help.',
      open: ['Meeting goal, materials and 10-minute prep', 'Your opening words, word for word', block],
      activity: ['Calm, simple steps, from gloves on to the emergency call', '3 Fix It cues for the most common problems', 'Safety box and age level card for ages 8–9 and 10–11'],
      close: close3,
    },
    {
      tab: 'Getting ready for camp',
      n: 'MEETING 06 · MEETING 18',
      title: 'Tent & Camp Setup: Pitch, Pack & Choose a Site',
      goal: 'As a team, choose a safe tent spot, pitch a tent and pack it back into its bag.',
      open: ['Meeting goal, materials and 10-minute prep', 'Your opening words, word for word', block],
      activity: ['Steps from choosing the spot to packing the tent', '3 Fix It cues for the most common problems', 'Age level card for ages 8–9 and 10–11'],
      close: close3,
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE IN YOUR HAND AT THE MEETING.',
  sub: 'Open any page and read it at full size: your 40-meeting plan, a complete meeting in 3 pages, the outdoor challenge rally and your meeting planner.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: [
    'Your 40-meeting plan (Q3, with the camp)',
    'Meeting 08 · meeting plan',
    'Meeting 08 · skill time',
    'Meeting 08 · challenge & closing',
    'Meeting 10 · rally stations',
    'Meeting planner + skills tracker',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY MEETING',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST SHOW UP AND RUN IT.',
  text: 'All 40 meetings are mapped: the start of one of the 10 complete meetings, a ready skill and game, an outing or camp, or a skills check night. Each complete meeting gives you the plan, the skill steps and the challenge & closing in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 40-meeting plan.' },
    { title: 'PREP', text: 'Read the page and set up in 10 minutes.' },
    { title: 'RUN', text: 'Opening game, one outdoor skill, a team challenge.' },
    { title: 'CLOSE', text: 'Closing game, closing circle and the family note.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE MEETING',
  features: [
    { icon: 'goal', title: 'One clear meeting goal', text: 'One sentence of what the kids will do, the meeting to start it and what they need first.' },
    { icon: 'timer', title: 'A 90-minute meeting block', text: 'Welcome & opening game 15 · Skill time 35 · Team challenge 20 · Closing game 10 · Closing circle 10.' },
    { icon: 'steps', title: 'Skill steps + Fix It cues', text: 'Numbered steps with diagrams and key points, and a quick cue for the most common problems.' },
    { icon: 'questions', title: 'Ages 8–9 and 10–11', text: 'How to run the same meeting with younger and older kids in the same group.' },
    { icon: 'quote', title: 'Your words, word for word', text: 'Opening words, signals and the closing circle, plus a pro tip and a common mistake.' },
    { icon: 'action', title: 'Challenge, games + family note', text: 'A team challenge and a closing game with diagrams, a skill check and a ready-to-send note for families.' },
  ],
} as const;

// ── 5b. SKILLS + AGE LEVELS (new section in this landing) ──────────────────
export const skillsBand = {
  eyebrow: 'REAL OUTDOOR SKILLS',
  title: 'ONE SKILL FOR EVERY MEETING. ALL YEAR LONG.',
  sub: 'Kids learn by doing: every complete meeting builds one skill they can show on the next skills check night.',
  skills: [
    { n: '01', skill: 'Teamwork' },
    { n: '02', skill: 'Ropework' },
    { n: '03', skill: 'First Aid' },
    { n: '04', skill: 'Navigation' },
    { n: '05', skill: 'Outdoor Readiness' },
    { n: '06', skill: 'Campcraft' },
    { n: '07', skill: 'Camp Cooking' },
    { n: '08', skill: 'Nature' },
    { n: '09', skill: 'Community Service' },
    { n: '10', skill: 'Leadership' },
  ],
  agesTitle: 'EVERY MEETING ADAPTS TO THE KIDS IN FRONT OF YOU',
  ages: [
    { age: 'AGES 8–9', text: 'Picture checklists, shorter steps, more games and a leader close by.' },
    { age: 'AGES 10–11', text: 'The full skill, leading their team and helping the younger kids.' },
  ],
};

// ── 6. THE 40-MEETING PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 40-MEETING YEAR PLAN',
  title: 'THE WHOLE YEAR, MAPPED MEETING BY MEETING.',
  sub: '★ starts a new complete meeting. ▲ is an outing or camp. ◆ is a skills check & award night. Every other meeting has a ready skill and game.',
  unitLabel: 'MEETING',
  quarters: [
    {
      q: 'Q1',
      range: 'MEETINGS 1–10',
      name: 'START STRONG',
      full: [
        { n: '1', kind: 'start', label: 'START MEETING 01', title: 'Welcome Night' },
        { n: '4', kind: 'start', label: 'START MEETING 02', title: 'Knot Night' },
        { n: '7', kind: 'start', label: 'START MEETING 03', title: 'First Aid 1' },
        { n: '9', kind: 'outing', label: 'OUTING', title: 'Neighborhood Walk' },
        { n: '10', kind: 'review', label: 'SKILLS CHECK', title: 'Award night with the tracker' },
      ],
      activities: '+ ready skills and games, like Name Chain, Rule Charades and Knot Speed Check.',
    },
    {
      q: 'Q2',
      range: 'MEETINGS 11–20',
      name: 'EXPLORE OUTDOORS',
      full: [
        { n: '11', kind: 'start', label: 'START MEETING 04', title: 'Find Your Way: Map & Compass' },
        { n: '14', kind: 'start', label: 'START MEETING 05', title: 'Pack It Right' },
        { n: '18', kind: 'start', label: 'START MEETING 06', title: 'Tent & Camp Setup' },
        { n: '20', kind: 'review', label: 'SKILLS CHECK', title: 'Award night with the tracker' },
      ],
      activities: '+ Compass Points, Treasure Map, Weather Dress-Up Relay and a thank-you card night.',
    },
    {
      q: 'Q3',
      range: 'MEETINGS 21–30',
      name: 'CAMP READY',
      full: [
        { n: '21', kind: 'start', label: 'START MEETING 07', title: 'Trail Food & Camp Kitchen Safety' },
        { n: '24', kind: 'start', label: 'START MEETING 08', title: 'Nature Detectives' },
        { n: '28', kind: 'start', label: 'START MEETING 09', title: 'Helping Hands' },
        { n: '29', kind: 'outing', label: 'CAMP', title: 'Weekend camp or day camp' },
        { n: '30', kind: 'review', label: 'SKILLS CHECK', title: 'Award night with the tracker' },
      ],
      activities: '+ Menu Builder, Nature Bingo, Star Cards and Camp Prep Night.',
    },
    {
      q: 'Q4',
      range: 'MEETINGS 31–40',
      name: 'LEAD THE WAY',
      full: [
        { n: '31', kind: 'start', label: 'START MEETING 10', title: 'Outdoor Challenge Rally' },
        { n: '33', kind: 'outing', label: 'OUTING', title: 'Helping Hands Day' },
        { n: '40', kind: 'review', label: 'YEAR-END', title: 'Ceremony & awards' },
      ],
      activities: '+ Leader for a Day, Tripod Build, Mini Orienteering and Family Night.',
    },
  ],
  toolsTitle: 'PLUS YOUR LEADER TOOLS',
  tools: ['When the meeting gets hard: 8 situations, what to say and do', 'Meeting planner + skills tracker: plan any meeting in 10 minutes', 'Leader field card to keep in your pocket'],
};

// ── 7. THE 10 COMPLETE MEETINGS ─────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE MEETINGS',
  title: 'TEN SCOUT MEETINGS. READY TO RUN.',
  sub: 'Follow the 40-meeting plan, or start with the meeting your group needs now. Each one is 3 pages: meeting plan, skill time, challenge & closing.',
  weekLabel: 'START AT MEETING',
  items: [
    { n: '01', week: '1', title: 'Welcome Night: Team Up & Set Our Group Rules', goal: 'Teams, names, the group rules and the quiet signal.' },
    { n: '02', week: '4', title: 'Knot Night: 4 Knots Every Scout Uses', goal: 'Overhand, square, figure-eight and clove hitch, and what each one is for.' },
    { n: '03', week: '7', title: 'First Aid 1: Cuts, Scrapes, Stings & Getting Help', goal: 'Check, call, care, a pretend bandage and the emergency call.' },
    { n: '04', week: '11', title: 'Find Your Way: Map & Compass Basics', goal: 'The parts of a compass, finding north and walking a bearing.' },
    { n: '05', week: '14', title: 'Pack It Right: Day-Hike Essentials & Layers', goal: 'Pack a day pack with the essentials and dress in 3 layers.' },
    { n: '06', week: '18', title: 'Tent & Camp Setup: Pitch, Pack & Choose a Site', goal: 'Choose a safe spot, pitch a tent as a team and pack it back.' },
    { n: '07', week: '21', title: 'Trail Food & Camp Kitchen Safety', goal: 'Clean hands, a no-cook trail snack and a team cleanup.' },
    { n: '08', week: '24', title: 'Nature Detectives: Tracks, Trees & Leave It Better', goal: '3 trees, 3 animal tracks and a clean-up challenge.' },
    { n: '09', week: '28', title: 'Helping Hands: Plan a Community Service Project', goal: 'Choose a real need, plan the project and give every kid a job.' },
    { n: '10', week: '31', title: 'Outdoor Challenge Rally: 5 Skill Stations', goal: 'Knots, first aid, compass, packing and nature stations, plus the Lost Hiker Mission.' },
  ],
  cta: 'GET ALL 10 MEETINGS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 7b. FOR GROUP COORDINATORS (new section in this landing) ──────────────────
export const coordinators = {
  eyebrow: 'FOR GROUP COORDINATORS',
  title: 'ONE PLAN FOR EVERY LEADER IN YOUR GROUP.',
  sub: 'Running a group with several volunteer leaders? Hand them the same page, and every meeting runs the same way.',
  points: [
    { title: 'Same plan, every leader', text: 'Every leader opens the same meeting page: same goal, same timing, same words.' },
    { title: 'A substitute can run it tonight', text: 'If a leader can’t make it, anyone can pick up this week’s page and run the meeting.' },
    { title: 'Families get the same message', text: 'Every meeting ends with a ready-to-send family note, so parents know what their kid learned.' },
    { title: 'No more 10 p.m. planning', text: 'Your leaders volunteered to lead kids, not to plan meetings late at night after work.' },
  ],
  cta: 'GET THE PLAN FOR MY GROUP',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 BOOK',
  title: 'EVERYTHING ELSE YOUR MEETINGS NEED.',
  sub: 'Games, rainy-day meetings, camp checklists, family letters and ceremony scripts, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '50 Opening & Closing Games',
      hook: 'Never run out of games again.',
      title: '50 Opening & Closing Games — No Materials Needed',
      text: '50 games to open, energize and close any meeting, organized by goal: icebreakers, team builders, quiet-down games and outdoor games. Each with setup, rules, time and a safety tip.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: '10 Rainy-Day Meetings',
      hook: 'Bad weather? The meeting is still ready.',
      title: '10 Rainy-Day Meetings — Ready-to-Run Indoor Plans',
      text: '10 complete indoor meetings for when an outing is canceled or the weather turns: the same 90-minute format, simple materials and indoor versions of outdoor skills.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'Camp & Outing Checklist Kit',
      hook: 'Leave nothing behind.',
      title: 'Camp & Outing Checklist Kit — Packing Lists, Gear Checks & Run Sheets',
      text: 'Packing lists for kids and leaders, a group gear check, a first aid kit list, a day-of run sheet, a headcount sheet and a camp job chart, ready to print.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: 'Ready-to-Send Family Letters',
      hook: 'Know exactly what to send to families.',
      title: 'Ready-to-Send Family Letters — Welcome Letter, Outing Notices & Reminders',
      text: 'A welcome letter for new families, outing and camp notices, what-to-bring reminders, an allergy and medical info request, thank-you notes and end-of-year messages.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: 'Ceremony Scripts',
      hook: 'Make every milestone feel special.',
      title: 'Ceremony Scripts — Welcome, Promise & Moving-Up Ceremonies',
      text: 'Word-for-word scripts for welcome nights, promise ceremonies, award nights, moving-up ceremonies and the year-end celebration. Use your group’s own promise and awards.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT MEETING STARTS HERE',
  title: 'YOUR WHOLE YEAR OF SCOUT MEETINGS. ALL IN ONE BOOK.',
  kicker: 'THE COMPLETE DIGITAL BOOK',
  name: 'Scout Leader Ready™',
  description: 'A 40-meeting year plan and 10 complete, ready-to-run 90-minute meetings for kids 8–11, plus five bonuses: games, rainy-day meetings, camp checklists, family letters and ceremony scripts.',
  mockupAlt: 'Everything included in Scout Leader Ready™',
  includedTitle: 'YOUR BOOK INCLUDES',
  included: [
    '40-meeting year plan: the whole year mapped',
    '10 complete meetings, 3 pages each',
    'A timed 90-minute meeting block in every meeting',
    'Skill steps, key points and Fix It cues',
    'Ages 8–9 and 10–11 in every meeting',
    'Team challenge, closing game and family note',
    'Leader tools: hard moments, planner, field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Scout Leader Ready™ (40-meeting plan + 10 complete meetings)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · Less than 70¢ a meeting',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout (new copy for this landing) */
  cta: 'YES, I WANT MY YEAR PLANNED — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. Works with any association or program: use your group’s own promise, words and awards, and follow your organization’s safety rules.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Run your next meetings with it. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOUR NEXT MEETING.',
  sub: 'Built for volunteer leaders and coordinators of kids’ groups. A ready-to-run meeting book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF book: a 40-meeting year plan (every meeting has the start of a complete meeting, a ready skill and game, an outing or camp, or a skills check night) and 10 complete 90-minute meetings of 3 pages each, plus leader tools and 5 bonuses.',
    },
    {
      q: 'Does it work with my association or program?',
      a: 'Yes. It is generic: it works for any scout troop or pack, youth group or kids’ club. It does not use official badges, ranks or programs, and it is not affiliated with any association. Use your group’s own promise, words and awards.',
    },
    {
      q: 'What ages is it for?',
      a: 'Kids from 8 to 11. Every meeting has an age level card for ages 8–9 and 10–11, so it works for mixed groups too.',
    },
    {
      q: 'Do I need special equipment?',
      a: 'No. Simple, cheap materials: rope, a compass, bandages, paper and markers, cones, and a tent for the camp meetings. Every meeting has a “you need” list.',
    },
    {
      q: 'Indoors or outdoors?',
      a: 'Both. Most meetings work in a hall or outside, and the year plan includes outings, a camp and a meeting for when the weather cancels an outing.',
    },
    {
      q: 'Is it safe for kids?',
      a: 'Every meeting has a safety box. Kids never use knives, stoves or fire in this book, first aid is basic awareness (check, call, care), and outings follow your organization’s rules.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I share it with the other leaders in my group?',
      a: 'Yes, with the leaders of your own group. Redistributing it to other groups or reselling it is not included in this offer.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet during the meeting, and the meeting planner and skills tracker are ready to print.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT ARE WE DOING THIS WEEK?”',
  title: 'WALK INTO YOUR NEXT MEETING WITH EVERYTHING READY.',
  text: 'Open this week’s page. Prep in 10 minutes. Run a meeting the kids talk about all week.',
  microcopy: '40-meeting plan + 10 complete meetings + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'SCOUT LEADER READY™ · Meeting material for volunteer leaders. Not affiliated with any scouting association. Always follow your organization’s safety rules.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
